import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  usePropertiesAPI,
  UsePropertiesAPIReturn,
} from 'src/SharedCustomHooks/UsePropertiesAPI/usePropertiesAPI';
import { Property } from 'src/SharedCustomHooks/UseSuiteActions/types';
import {
  clearCountsDone,
  fetchPropertyCounts,
} from 'src/store/actions/properties';
import { getWalkthroughSuites } from 'src/store/actions/WalkthroughSuites';
import { clearActiveSuiteTab } from 'src/store/actions/activeTab';
import { selectCounts } from 'src/store/selectors/Counts';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { upload } from 'src/helpers/upLoadHelper';
import {
  useNotesAPI,
  UseNotesAPIReturn,
} from 'src/SharedCustomHooks/UseNotesAPI/useNotesAPI';
import { useTabsAPI, UseTabsAPIReturn } from 'src/SharedCustomHooks/UseTabsAPI/useTabsAPI';
import usePropertySuiteSelection, {
  UsePropertySuiteSelectionReturn,
} from 'src/pages/SharedPageCustomHooks/UsePropertySuiteSelection/usePropertySuiteSelection';
import { hasHomeOwnerVisibleNote } from 'src/SharedCustomHooks/UseFaqsNotesDocumentsAPI/helpers';
import { checkSparkDataAvailable } from 'src/helpers/validationHelper';
import { buildPropertyUpdateParams } from 'src/SharedCustomHooks/UsePropertiesAPI/helpers';

export interface IUsePropertyPageOrchestratorParams {
  propertyId: string;
  location: any;
}

export interface IUsePropertyPageOrchestratorReturn {
  property: Property | null;
  isLoadingProperty: boolean;
  isUpdatingProperty: boolean;
  isDeletingProperty: boolean;
  counts: any;
  conveyanceCsv: any[];
  tabList: any[];
  suites: any[];
  isLoadingSuites: boolean;
  refetchSuites: () => void;
  notesAPI: UseNotesAPIReturn;
  noteVisibleToHomeownerOccupantRealtor: boolean;
  serviceRequestId: string | null;
  navOrigin: any;
  linkView: boolean;
  canManageProperty: boolean;
  canDeleteProperty: boolean;
  showNotesTab: boolean;
  showPeopleTab: boolean;
  showConveyanceTab: boolean;
  isPropertyFormOpen: boolean;
  propertyFormInitialData: any;
  propertyFormInitialModulePermissions: any[];
  openPropertyForm: (data: any) => void;
  closePropertyForm: () => void;
  lightboxImage: string | null;
  openLightbox: (image: string) => void;
  closeLightbox: () => void;
  isDeleteModalOpen: boolean;
  openDeleteModal: () => void;
  closeDeleteModal: () => void;
  saveProperty: (formData: any) => Promise<{ ok: boolean }>;
  deleteProperty: (id: string) => Promise<{ ok: boolean }>;
}

const LINK_VIEW_ROLES: string[] = [
  'Admin',
  'Property Developer',
  'Strata Manager',
];

export const usePropertyPageOrchestrator = ({
  propertyId,
  location,
}: IUsePropertyPageOrchestratorParams): IUsePropertyPageOrchestratorReturn => {
  const dispatch = useDispatch();

  const user: any = useSelector((state: any) => selectAuthenticatedUser(state));

  const userId: string = user?.id ?? '';
  const userRole: string = user?.roles?.[0]?.role?.name ?? '';

  const serviceRequestId: string | null =
    location?.state?.data?.serviceRequestId ?? null;
  const navOrigin: any = location?.state?.navOrigin;
  const homeOwnerCheck: boolean = userRole === 'Home Owner';
  const linkView: boolean = LINK_VIEW_ROLES.includes(userRole);

  const isOccupant: boolean = userRole === 'Occupant';
  const isRealtor: boolean = userRole === 'Realtor';
  const isAdmin: boolean = userRole === 'Admin';
  const isSuperAdmin: boolean = userRole === 'Super Admin';
  const isPropertyDeveloper: boolean = userRole === 'Property Developer';

  const propertiesAPI: UsePropertiesAPIReturn = usePropertiesAPI();
  const notesAPI: UseNotesAPIReturn = useNotesAPI(user);
  const tabsAPI: UseTabsAPIReturn = useTabsAPI();
  const suitesSelection: UsePropertySuiteSelectionReturn =
    usePropertySuiteSelection({});

  const refetchSuites = (): void => {
    void suitesSelection.fetchPropertySuites(propertyId);
  };

  // UI state (doesn't need to be in a hook for now but if it grows good to extract this)
  const [isPropertyFormOpen, setIsPropertyFormOpen] = useState<boolean>(false);
  const [propertyFormInitialData, setPropertyFormInitialData] =
    useState<any>(undefined);
  const [
    propertyFormInitialModulePermissions,
    setPropertyFormInitialModulePermissions,
  ] = useState<any[]>([]);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const noteVisibleToHomeownerOccupantRealtor: boolean =
    hasHomeOwnerVisibleNote(notesAPI.notes);

  const isSparkDataLocked: boolean = propertiesAPI.property
    ? checkSparkDataAvailable(propertiesAPI.property, userRole)
    : false;

  const canManageProperty: boolean = !(
    isSparkDataLocked ||
    homeOwnerCheck ||
    isOccupant
  );

  const canDeleteProperty: boolean = isAdmin || isSuperAdmin;

  const showNotesTab: boolean = !(
    (homeOwnerCheck || isOccupant || isRealtor) &&
    !noteVisibleToHomeownerOccupantRealtor
  );

  const showPeopleTab: boolean = !(homeOwnerCheck || isOccupant);

  const showConveyanceTab: boolean =
    isAdmin || isSuperAdmin || isPropertyDeveloper;

  /* TODO: legacy BOS logic counts stays on the Redux Counts slice for now. It's a
   * multi-writer value: PropertyNotes, PropertySuites, usePropertyDocumentsAPI,
   * PropertyDevelopers, SuitesTable and useCommonAreas all dispatch
   * fetchPropertyCounts to refresh these badges after their own mutations.
   * Migrating counts to orchestrator-local state means threading a refreshCounts
   * callback into all of those cousins — wide blast radius, deferred to its own
   * ticket. The orchestrator owns the read + mount fetch so the page itself no
   * longer touches Redux for counts. */
  const counts: any = useSelector((state: any) => selectCounts(state));

  useEffect(() => {
    if (!propertyId) {
      return;
    }

    void propertiesAPI.fetchPropertyById(propertyId);
    void propertiesAPI.fetchConveyanceCsv(propertyId);
    void propertiesAPI.fetchAllowedTabList(propertyId);
    void notesAPI.fetchNotes(propertyId);
    void tabsAPI.fetchTabList();
    void suitesSelection.fetchPropertySuites(propertyId);
  }, [propertyId]);

  useEffect(() => {
    if (!propertyId || !userId || !userRole) {
      return;
    }

    dispatch(clearCountsDone());
    dispatch(fetchPropertyCounts(propertyId, userId, userRole));
  }, [propertyId, userId, userRole]);

  /* TODO: legacy BOS logic getWalkthroughSuites populates the Redux `walkthroughtSuites`
   * slice, which is read cross-page by ConfigurePropertyWalkThrough (that form
   * has no fetch of its own — this page is the sole dispatcher app-wide). The
   * walkthrough trees are a retiring zone, so this stays a Redux dispatch rather
   * than being migrated to a data hook; remove or relocate when walkthrough is
   * retired/modernized */
  useEffect(() => {
    if (!propertyId) {
      return;
    }

    dispatch(getWalkthroughSuites(propertyId));
  }, [propertyId]);

  /* TODO(tabs-active-state): legacy BOS logic `activeTab` is a shared cross-page Redux bus the
   * Tabs component reads/writes. We clear it on unmount so the active-tab index
   * doesn't leak into the next page's Tabs. Decoupling means Tabs owning
   * per-instance active state — wide blast radius (shared Tabs + ~8 pages),
   * deferred to its own ticket. */
  useEffect(() => {
    return () => {
      dispatch(clearActiveSuiteTab());
    };
  }, []);

  const saveProperty = async (formData: any): Promise<{ ok: boolean }> => {
    if (!formData.propertyId) {
      console.warn('saveProperty called with no propertyId');
      return { ok: false };
    }

    const imageUrl: string | null =
      formData.selectedImage && formData.selectedImage.length > 0
        ? await upload(formData.selectedImage[0])
        : null;

    const params: any = buildPropertyUpdateParams(formData, userId, imageUrl);

    const { ok } = await propertiesAPI.updateProperty(params);

    if (!ok) {
      return { ok };
    }

    await Promise.all([
      propertiesAPI.fetchPropertyById(formData.propertyId),
      propertiesAPI.fetchAllowedTabList(formData.propertyId),
    ]);

    return { ok };
  };

  const openPropertyForm = (data: any): void => {
    /* legacy/workaround: the GET Properties/{id} endpoint omits modulePermissions
     * (only the list endpoint populates it), so the property loaded here always has
     * modulePermissions: null. We instead source the property's enabled modules from
     * the ModulePermissions/{id} fetch (propertiesAPI.allowedTabList) to pre-check the form.
     * TODO: drop this once the by-id endpoint returns modulePermissions — API can't be changed atm. */
    const modulePermissionRefs: any[] = propertiesAPI.allowedTabList.map(
      (item: any) => ({ Id: item.id })
    );

    setPropertyFormInitialData(data);
    setPropertyFormInitialModulePermissions(modulePermissionRefs);
    setIsPropertyFormOpen(true);
  };

  const closePropertyForm = (): void => {
    setIsPropertyFormOpen(false);
    setPropertyFormInitialData(undefined);
    setPropertyFormInitialModulePermissions([]);
  };

  const openLightbox = (image: string): void => {
    if (!image) {
      return;
    }

    setLightboxImage(image);
  };

  const closeLightbox = (): void => setLightboxImage(null);

  const openDeleteModal = (): void => setIsDeleteModalOpen(true);

  const closeDeleteModal = (): void => setIsDeleteModalOpen(false);

  const deleteProperty = async (id: string): Promise<{ ok: boolean }> => {
    if (!id || !userId) {
      console.warn('deleteProperty called without id or userId');
      return { ok: false };
    }

    return propertiesAPI.deleteProperty(id, userId);
  };

  return {
    property: propertiesAPI.property,
    isLoadingProperty: propertiesAPI.isLoadingProperty,
    isUpdatingProperty: propertiesAPI.isUpdatingProperty,
    isDeletingProperty: propertiesAPI.isDeletingProperty,
    counts,
    conveyanceCsv: propertiesAPI.conveyanceCsv,
    tabList: tabsAPI.tabList,
    suites: suitesSelection.data.suitesList,
    isLoadingSuites: suitesSelection.loading.propertySuite,
    refetchSuites,
    notesAPI,
    noteVisibleToHomeownerOccupantRealtor,
    serviceRequestId,
    navOrigin,
    linkView,
    canManageProperty,
    canDeleteProperty,
    showNotesTab,
    showPeopleTab,
    showConveyanceTab,
    isPropertyFormOpen,
    propertyFormInitialData,
    propertyFormInitialModulePermissions,
    openPropertyForm,
    closePropertyForm,
    lightboxImage,
    openLightbox,
    closeLightbox,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    saveProperty,
    deleteProperty,
  };
};